import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { SectionCard } from '@/components/shared/section-card'
import { Trophy, Users, Activity, Heart, Zap, Moon, Timer, AlertCircle, LifeBuoy } from 'lucide-react'
import { formatNumber } from '@/lib/format'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useCollectionRecords } from '@/hooks'
import { SupportTicket } from '@/types'
import { usePlatformDailyStats, useStepsLeaderboard } from '@/hooks/useDashboardAggregates'

export function AnalyticsPage() {
  const dailyStats = usePlatformDailyStats(30)
  const leaderboard = useStepsLeaderboard(7, 1)
  const tickets30d = useCollectionRecords<SupportTicket>(['supportTickets'], {
    orderByField: 'updatedAt',
    direction: 'desc',
    limitCount: 200,
  })

  const ticketCount30d = useMemo(() => {
    const since = Date.now() / 1000 - 30 * 24 * 60 * 60
    return (tickets30d.data ?? []).filter((t) => {
      const ts: any = t.updatedAt
      return ts?.seconds ? ts.seconds >= since : false
    }).length
  }, [tickets30d.data])

  const metrics = useMemo(() => {
    const data = dailyStats.data || []

    if (data.length === 0) {
      return {
        avgSteps: 0,
        avgSleepHrs: 0,
        activeUsers: 0,
        totalWorkouts: 0,
        topPerformer: '-',
        stepsTrend: 0,
        sleepTrend: 0,
        usersTrend: 0,
        workoutsTrend: 0,
      }
    }

    const totalAvgSteps = data.reduce((acc, d) => acc + d.avgSteps, 0)
    const avgSteps = Math.round(totalAvgSteps / data.length)

    const totalAvgSleep = data.reduce((acc, d) => acc + d.avgSleep, 0)
    const avgSleepMin = data.length > 0 ? totalAvgSleep / data.length : 0
    const avgSleepHrs = Math.round((avgSleepMin / 60) * 10) / 10

    const today = data[data.length - 1]
    const activeUsers = today?.activeUsers ?? 0

    const last7 = data.slice(-7)
    const prev7 = data.slice(-14, -7)
    const trend = (last: number[], prev: number[]) => {
      const lAvg = last.length > 0 ? last.reduce((a, b) => a + b, 0) / last.length : 0
      const pAvg = prev.length > 0 ? prev.reduce((a, b) => a + b, 0) / prev.length : 0
      return pAvg > 0 ? Math.round(((lAvg - pAvg) / pAvg) * 100) : 0
    }
    const stepsTrend = trend(last7.map((d) => d.avgSteps), prev7.map((d) => d.avgSteps))
    const sleepTrend = trend(last7.map((d) => d.avgSleep), prev7.map((d) => d.avgSleep))
    const usersTrend = trend(last7.map((d) => d.activeUsers), prev7.map((d) => d.activeUsers))
    const workoutsTrend = trend(last7.map((d) => d.workouts), prev7.map((d) => d.workouts))

    const totalWorkouts = data.reduce((acc, d) => acc + d.workouts, 0)
    const topPerformer = leaderboard.data?.[0]?.displayName || '-'

    return {
      avgSteps,
      avgSleepHrs,
      activeUsers,
      totalWorkouts,
      topPerformer,
      stepsTrend,
      sleepTrend,
      usersTrend,
      workoutsTrend,
    }
  }, [dailyStats.data, leaderboard.data])

  const stats = [
    {
      title: 'Avg Daily Steps',
      value: formatNumber(metrics.avgSteps),
      icon: Zap,
      trend: `${metrics.stepsTrend > 0 ? '+' : ''}${metrics.stepsTrend}%`,
      trendUp: metrics.stepsTrend >= 0,
      color: 'orange',
      subtitle: 'Per active user',
    },
    {
      title: 'Avg Sleep Duration',
      value: `${metrics.avgSleepHrs} hrs`,
      icon: Moon,
      trend: `${metrics.sleepTrend > 0 ? '+' : ''}${metrics.sleepTrend}%`,
      trendUp: metrics.sleepTrend >= 0,
      color: 'indigo',
      subtitle: 'Per night average',
    },
    {
      title: 'Active Users Today',
      value: formatNumber(metrics.activeUsers),
      icon: Users,
      trend: `${metrics.usersTrend > 0 ? '+' : ''}${metrics.usersTrend}%`,
      trendUp: metrics.usersTrend >= 0,
      color: 'emerald',
      subtitle: 'vs last week',
    },
    {
      title: 'Workouts (30d)',
      value: formatNumber(metrics.totalWorkouts),
      icon: Timer,
      trend: `${metrics.workoutsTrend > 0 ? '+' : ''}${metrics.workoutsTrend}%`,
      trendUp: metrics.workoutsTrend >= 0,
      color: 'amber',
      subtitle: 'Total sessions',
    },
    {
      title: 'Support Tickets (30d)',
      value: formatNumber(ticketCount30d),
      icon: LifeBuoy,
      trend: '',
      trendUp: true,
      color: 'rose',
      subtitle: 'Recently logged',
    },
    {
      title: 'Top Performer',
      value: metrics.topPerformer,
      icon: Trophy,
      trend: '',
      trendUp: true,
      color: 'amber',
      subtitle: '7-day step leader',
    },
  ]

  const insights = useMemo(() => {
    const data = dailyStats.data || []
    if (data.length === 0) return null
    return {
      totalWorkouts: data.reduce((acc, d) => acc + d.workouts, 0),
      totalSteps: data.reduce((acc, d) => acc + d.steps, 0),
      totalSleepHrs: Math.round(data.reduce((acc, d) => acc + d.sleep, 0) / 60),
      totalActiveUserDays: data.reduce((acc, d) => acc + d.activeUsers, 0),
    }
  }, [dailyStats.data])

  if (dailyStats.error) {
    return (
      <div className="rounded-3xl border border-rose-500/20 bg-rose-500/5 p-8 text-center">
        <AlertCircle className="mx-auto mb-4 h-10 w-10 text-rose-500" />
        <p className="text-lg font-semibold text-white">Aggregation failed</p>
        <p className="mt-2 text-sm text-stone-400">
          {dailyStats.error.message}. Make sure Firestore allows{' '}
          <code className="rounded bg-stone-800 px-1.5 py-0.5 text-xs">collectionGroup('health')</code>{' '}
          queries (may require composite index on `date`).
        </p>
      </div>
    )
  }

  if (dailyStats.loading && (!dailyStats.data || dailyStats.data.length === 0)) {
    return (
      <div className="rounded-3xl border border-stone-800 bg-stone-900/50 p-12 text-center">
        <p className="text-stone-400">Aggregating data from all users…</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((stat, i) => (
          <div key={i} className="rounded-3xl border border-stone-800 bg-[#1a1a1a] p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className={cn(
                'rounded-2xl p-2.5',
                stat.color === 'orange' ? 'bg-orange-500/10' :
                stat.color === 'indigo' ? 'bg-indigo-500/10' :
                stat.color === 'emerald' ? 'bg-emerald-500/10' :
                stat.color === 'rose' ? 'bg-rose-500/10' :
                'bg-amber-500/10',
              )}>
                <stat.icon className={cn(
                  'h-5 w-5',
                  stat.color === 'orange' ? 'text-orange-500' :
                  stat.color === 'indigo' ? 'text-indigo-500' :
                  stat.color === 'emerald' ? 'text-emerald-500' :
                  stat.color === 'rose' ? 'text-rose-500' :
                  'text-amber-500',
                )} />
              </div>
              {stat.trend && (
                <span className={cn(
                  'text-xs font-bold',
                  stat.trendUp ? 'text-emerald-500' : 'text-rose-500',
                )}>
                  {stat.trend}
                </span>
              )}
            </div>
            <p className="mt-4 text-sm text-stone-500">{stat.title}</p>
            <p className="mt-1 text-2xl font-bold text-white truncate">{stat.value}</p>
            <p className="mt-1 text-xs text-stone-500">{stat.subtitle}</p>
          </div>
        ))}
      </div>

      {/* Steps + Sleep + Workouts main chart */}
      <SectionCard
        title="User Activity Trends (30 days)"
        description="Steps, sleep, and workouts per day across all synced users."
      >
        <div className="h-[400px] w-full pt-4">
          <ResponsiveContainer height="100%" width="100%">
            <AreaChart data={dailyStats.data}>
              <defs>
                <linearGradient id="colorSteps" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorAvgSleep" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorWorkoutsMain" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#737373', fontSize: 12 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#737373', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#171717',
                  border: '1px solid #262626',
                  borderRadius: '16px',
                  color: '#fff',
                }}
              />
              <Area
                type="monotone"
                dataKey="avgSteps"
                stroke="#f97316"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorSteps)"
                name="Avg Steps"
              />
              <Area
                type="monotone"
                dataKey="avgSleep"
                stroke="#6366f1"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorAvgSleep)"
                name="Avg Sleep (min)"
              />
              <Area
                type="monotone"
                dataKey="workouts"
                stroke="#8b5cf6"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorWorkoutsMain)"
                name="Workouts"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      {/* Secondary Charts */}
      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          title="Active Users Trend"
          description="Distinct users with synced data per day"
        >
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyStats.data}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#737373', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#737373', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#171717',
                    border: '1px solid #262626',
                    borderRadius: '16px',
                    color: '#fff',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="activeUsers"
                  stroke="#10b981"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorUsers)"
                  name="Active Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard
          title="Workouts Trend"
          description="Total workout sessions per day"
        >
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyStats.data}>
                <defs>
                  <linearGradient id="colorWorkouts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#737373', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#737373', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#171717',
                    border: '1px solid #262626',
                    borderRadius: '16px',
                    color: '#fff',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="workouts"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorWorkouts)"
                  name="Workouts"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Summary Stats */}
      {insights && (
        <SectionCard title="30-Day Summary" description="Aggregated across all synced users">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-5 w-5 text-orange-500" />
                <span className="text-sm text-stone-500">Total Steps</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(insights.totalSteps)}</p>
            </div>
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="h-5 w-5 text-indigo-500" />
                <span className="text-sm text-stone-500">Total Sleep (hrs)</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(insights.totalSleepHrs)}</p>
            </div>
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="h-5 w-5 text-amber-500" />
                <span className="text-sm text-stone-500">Total Workouts</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(insights.totalWorkouts)}</p>
            </div>
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-5 w-5 text-emerald-500" />
                <span className="text-sm text-stone-500">User-Days Active</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(insights.totalActiveUserDays)}</p>
            </div>
          </div>
        </SectionCard>
      )}

      {dailyStats.data.length === 0 && (
        <div className="rounded-3xl border border-stone-800 bg-stone-900/50 p-12 text-center">
          <Heart className="mx-auto mb-4 h-10 w-10 text-stone-600" />
          <p className="text-lg font-semibold text-white">No health data yet</p>
          <p className="mt-2 text-sm text-stone-500">
            Once users sync their watch data, aggregated metrics will appear here.
          </p>
        </div>
      )}
    </div>
  )
}
